import { IonContent } from '@ionic/react';
import { FC, useState } from 'react';
import { AllieItem, AllieText } from '../../../../../components';
import {
  AllieBanHexa,
  AllieChatboxOutlineIcon,
  AllieCreateIcon,
  AllieRadioOffIcon,
} from '../../../../../styling/icons';
import { AllieButtonStyled, PopoverAddMotiveStyled } from './PopoverAddMotive.styles';
import {
  PopoverAddMotiveControl,
  PopoverAddMotiveLabelsProps,
  PopoverAddMotiveProps,
} from './PopoverAddMotive.types';
import { TimeRangeDisplay } from '../TimeRangeDisplay';

const shouldRenderAddMotiveButton = (isEdit: boolean, stopType: string) =>
  !isEdit || stopType !== 'technical';

const shouldRenderAddTechnicalStopButton = (
  showAddTechnicalStopMenu: boolean,
  isEdit: boolean,
  stopType: string
) => showAddTechnicalStopMenu && (!isEdit || stopType === 'technical');

const ItemOption = ({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}) => {
  return (
    <AllieItem button lines="none" detail={false} onclick={onClick}>
      <AllieButtonStyled full allieSize="xs" allieType="secondary" corner="rounded-edges-plus">
        <div className="button-container">
          {children}
          <AllieText type="body3" color="primary-60" style={{ whiteSpace: 'nowrap' }}>
            {label}
          </AllieText>
        </div>
      </AllieButtonStyled>
    </AllieItem>
  );
};

export const PopoverAddMotive: FC<
  PopoverAddMotiveProps & PopoverAddMotiveLabelsProps & PopoverAddMotiveControl
> = ({
  popRef,
  onClickOpenModalReason,
  handleCloseModal,
  timeRange,
  labels,
  id,
  timezone,
  isEdit,
  reason,
  stopReasonId,
  machineId,
  machineName,
  componentId,
  stopType,
  stopTypeName,
  isOpen,
  setIsOpen,
  isResized,
  originalTimeRange,
  showAddTechnicalStopMenu,
  categoryName,
  note,
  failureModeId,
  failureModeName,
  onClickAddTechnicalStop,
  renderAssignReasonForm,
  onClickDeleteStop,
}) => {
  const [isOpenModalReason, setIsOpenModalReason] = useState(false);
  const handleOpen = onClickOpenModalReason;

  const handleOpenModalReason = () => {
    setIsOpenModalReason(true);
  };

  const getBreadcrumb = () => {
    if (stopReasonId) {
      const arr = [stopTypeName, categoryName, machineName];
      return arr.filter(Boolean).join(' > ');
    }
    return '';
  };

  const reasonWithFailureMode =
    reason && failureModeName ? `${reason} - ${failureModeName}` : reason;

  const IconEditCreate = isEdit ? AllieCreateIcon : AllieChatboxOutlineIcon;

  const showOnlyDeleteStopButton = !!onClickDeleteStop;

  return (
    <PopoverAddMotiveStyled
      ref={popRef}
      className="timegrid-popover"
      reference="event"
      isOpen={isOpen}
      arrow
      showBackdrop={false}
      size="auto"
      mode="ios"
      onDidDismiss={() => setIsOpen(false)}
      isEdit={isEdit}
    >
      <IonContent className="ion-padding">
        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '10px' }}>
          <TimeRangeDisplay
            timeRange={timeRange}
            timezone={timezone}
            reason={reasonWithFailureMode}
            labels={labels}
            breadcrumb={getBreadcrumb()}
          />
          <div>
            {!showOnlyDeleteStopButton && shouldRenderAddMotiveButton(isEdit, stopType) && (
              <ItemOption
                onClick={() => handleOpen(handleOpenModalReason, isResized)}
                label={isEdit ? labels.editMotive : labels.addMotive}
              >
                <IconEditCreate
                  className="fill"
                  slot="start"
                  color="primary"
                  style={{ fontSize: '20px' }}
                />
              </ItemOption>
            )}
            {!showOnlyDeleteStopButton &&
              shouldRenderAddTechnicalStopButton(!!showAddTechnicalStopMenu, isEdit, stopType) && (
                <ItemOption
                  onClick={() =>
                    onClickAddTechnicalStop(id, timeRange, handleCloseModal, stopReasonId)
                  }
                  label={isEdit ? labels.editMotive : labels.addTechnicalStop}
                >
                  <AllieRadioOffIcon
                    className="fill"
                    slot="start"
                    color="primary"
                    style={{ fontSize: '20px' }}
                  />
                </ItemOption>
              )}
            {showOnlyDeleteStopButton && (
              <ItemOption
                onClick={() => onClickDeleteStop(id, handleCloseModal)}
                label={labels.deleteStop}
              >
                <AllieBanHexa slot="start" />
              </ItemOption>
            )}
          </div>
        </div>
        {isOpenModalReason &&
          renderAssignReasonForm({
            data: {
              id: id,
              timeRange: timeRange,
              originalTimeRange: originalTimeRange,
              stopType: stopType,
              stopReasonId: stopReasonId,
              machineId: machineId,
              componentId: componentId,
              note,
              failureModeId,
            },
            onCloseModal: handleCloseModal,
            isOpen: isOpenModalReason,
            setIsOpen: setIsOpenModalReason,
            isEdit: isEdit,
          })}
      </IonContent>
    </PopoverAddMotiveStyled>
  );
};
